import { memo, useCallback, useRef } from 'react'

import type { ModalRefsProps } from 'components/modal'
import { BestChoice } from 'modules/best-choice'
import { CondominiumCreditForm } from 'modules/condominium-credit-form'
import { Layout } from 'modules/layout'
import { pushDataLayer } from 'utils/push-data-layer'

import { CondominiumCreditAbout } from './about-credit'
import { CondominiumCreditLinesComparison } from './credit-lines-comparison'
import { CondominiumCreditHelp } from './help'
import { CondominiumCreditHero } from './hero'
import { CondominiumCreditHowItWorks } from './how-it-works'
import { CondominiumCreditLifeProject } from './life-project'
import { CondominiumCreditOtherProducts } from './other-products'
import { CondominiumCreditProjectUses } from './project-uses'

type SimulationSection =
  | 'Banner'
  | 'O que é'
  | 'Como funciona'
  | 'Projetos'
  | 'Linhas de crédito'
  | 'Projetos de vida'
  | 'Melhor escolha'
  | 'FAQ'

export const CondominiumCredit = memo(() => {
  const formRef = useRef<ModalRefsProps>(null)

  const handleOpenSimulation = useCallback((section: SimulationSection, label: string) => {
    pushDataLayer({
      category: 'Clique',
      action: 'Botões',
      label: `${section} - Crédito para Condomínios - ${label}`
    })

    formRef.current?.openModal()
  }, [])

  const handleHeroClick = useCallback(
    () => handleOpenSimulation('Banner', 'Solicitar uma simulação'),
    [handleOpenSimulation]
  )
  const handleAboutClick = useCallback(
    () => handleOpenSimulation('O que é', 'Solicitar uma simulação'),
    [handleOpenSimulation]
  )
  const handleHowItWorksClick = useCallback(
    () => handleOpenSimulation('Como funciona', 'Solicitar uma simulação'),
    [handleOpenSimulation]
  )
  const handleProjectUsesClick = useCallback(
    () => handleOpenSimulation('Projetos', 'Solicitar uma simulação'),
    [handleOpenSimulation]
  )
  const handleCreditLinesClick = useCallback(
    () => handleOpenSimulation('Linhas de crédito', 'Simular Agora'),
    [handleOpenSimulation]
  )
  const handleLifeProjectClick = useCallback(
    () => handleOpenSimulation('Projetos de vida', 'Solicitar uma simulação'),
    [handleOpenSimulation]
  )
  const handleBestChoiceClick = useCallback(
    () => handleOpenSimulation('Melhor escolha', 'Simular agora'),
    [handleOpenSimulation]
  )
  const handleHelpClick = useCallback(() => handleOpenSimulation('FAQ', 'Simular agora'), [handleOpenSimulation])

  return (
    <Layout hasCTA darkWarning>
      <CondominiumCreditHero onCtaClick={handleHeroClick} />
      <CondominiumCreditAbout onCtaClick={handleAboutClick} />
      <CondominiumCreditHowItWorks onCtaClick={handleHowItWorksClick} />
      <CondominiumCreditProjectUses onCtaClick={handleProjectUsesClick} />
      <CondominiumCreditLinesComparison onCtaClick={handleCreditLinesClick} />
      <CondominiumCreditLifeProject onCtaClick={handleLifeProjectClick} />
      <BestChoice
        onClick={handleBestChoiceClick}
        highlightColor="#FA0890"
        ctaAriaLabel="Solicitar uma simulação de crédito para condomínio"
        scrollToHeroOnDesktop={false}
      />
      <CondominiumCreditHelp onClick={handleHelpClick} />
      <CondominiumCreditOtherProducts />
      <CondominiumCreditForm ref={formRef} />
    </Layout>
  )
})

CondominiumCredit.displayName = 'CondominiumCredit'
